/**
 * 缓存策略服务（Web 应用版本）
 * 实现先返回缓存、异步更新的逻辑。
 *
 * 说明：待办/分组列表的服务端数据已由 React Query 管理（queryFn 使用 noCache 路径）。
 * 本 getWithCache 仅用于未传 noCache 的调用路径，作为兼容保留。
 */

import { getCache, setCache } from './cacheManager';

/**
 * 请求空闲回调的 polyfill
 */
function requestIdleCallbackPolyfill(callback: () => void, options?: { timeout?: number }): number {
  if (typeof requestIdleCallback !== 'undefined') {
    return requestIdleCallback(callback, options);
  }
  // Fallback to setTimeout
  const timeout = options?.timeout || 0;
  return setTimeout(callback, timeout) as unknown as number;
}

/**
 * 深度比较两个对象是否相等
 */
function deepEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    // 如果序列化失败，使用浅比较
    return false;
  }
}

/**
 * 先返回缓存，异步更新
 * 
 * @param cacheKey 缓存键
 * @param fetchFn 获取数据的函数
 * @param onUpdate 数据更新时的回调（可选）
 * @param cacheTimeout 缓存超时时间（毫秒），默认 5 分钟
 * @returns 数据（优先返回缓存）
 */
export async function getWithCache<T>(
  cacheKey: string,
  fetchFn: () => Promise<T>,
  onUpdate?: (data: T) => void,
  cacheTimeout: number = 5 * 60 * 1000 // 默认 5 分钟
): Promise<T> {
  // 1. 先尝试获取缓存
  const cached = await getCache<T>(cacheKey);
  
  if (cached) {
    const age = Date.now() - cached.timestamp;
    
    // 如果缓存未过期，直接返回
    if (age < cacheTimeout) {
      // 如果缓存已过期，在后台更新（不阻塞）
      if (age >= cacheTimeout) {
        requestIdleCallbackPolyfill(async () => {
          try {
            const freshData = await fetchFn();
            await setCache(cacheKey, freshData);
            
            // 如果数据有变化，调用更新回调
            if (!deepEqual(cached.data, freshData)) {
              if (onUpdate) {
                onUpdate(freshData);
              }
            }
          } catch (error) {
            console.warn(`[CacheStrategy] 后台更新缓存失败 [${cacheKey}]:`, error);
          }
        }, { timeout: 1000 });
      }
      
      return cached.data;
    }
  }
  
  // 2. 如果没有缓存或缓存已过期，获取新数据
  try {
    const freshData = await fetchFn();
    await setCache(cacheKey, freshData);
    
    // 如果有缓存且数据有变化，调用更新回调
    if (cached && !deepEqual(cached.data, freshData)) {
      if (onUpdate) {
        onUpdate(freshData);
      }
    }
    
    return freshData;
  } catch (error) {
    // 如果获取失败，尝试返回过期缓存
    if (cached) {
      console.warn(`[CacheStrategy] 获取数据失败，返回过期缓存 [${cacheKey}]:`, error);
      return cached.data;
    }
    
    // 没有缓存，抛出错误
    throw error;
  }
}
